import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  InputAdornment,
} from "@mui/material";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TuneIcon from "@mui/icons-material/Tune";

import dayjs from "dayjs";

export default function RelativeDateRangePicker({onReceiveProps, error, helperText}) {
  const [selectedRange, setSelectedRange] = useState("");
  const [customDays, setCustomDays] = useState('');
  const [showRelativeDateRangeOptions, setShowRelativeDateRangeOptions] = useState(false);

  const today = dayjs();

  const predefinedRanges = [
    {
      label: "Last 24 Hours",
      value: "T-1",
      days: 1,
    },
    {
      label: "Last 7 Days",
      value: "T-7",
      days: 7,
    },
    {
      label: "Last 30 Days",
      value: "T-30",
      days: 30,
    },
  ];

  const coverage = useMemo(() => {
    if(customDays){
      const startDate = today.subtract(customDays, "day");
      return {
        start: startDate.format("MMMM D, YYYY"),
        end: today.format("MMMM D, YYYY"),
      };
    }
  }, [customDays]);
  
  useEffect(() => {
    const handleDocumentClick = (event) => {
      const target = event.target;
      const lifecycleRangeElement = document.getElementById('active-lifecycle-range');

      if (lifecycleRangeElement && !lifecycleRangeElement.contains(target)) {
        setShowRelativeDateRangeOptions(false);
      }
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return (
    <Box id="active-lifecycle-range" sx={{ width: '100%', position: 'relative' }}>
      <label className="criteria-label" style={{ display: 'block', marginBottom: '8px' }}>Active Lifecycle Range *</label>

      {/* Input Field */}
      <TextField
        fullWidth
        value={customDays !== '' ? `T-${customDays}` : ""}
        placeholder="Select Range"
        error={error}
        helperText={helperText}
        FormHelperTextProps={{
          sx: {
            display: 'flex',
            alignItems: 'center',
            '&::before': { display: 'none' },
            '& svg': { display: 'none' }
          }
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonthIcon />
            </InputAdornment>
          ),
        }}
        onClick={() => setShowRelativeDateRangeOptions(!showRelativeDateRangeOptions)}
      />

      {/* Dropdown Panel */}
      {showRelativeDateRangeOptions && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            top: "81px",
            p: 2,
            position: 'absolute',
            width: "94%",
            zIndex: 11,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              mb: 1,
            }}
          >
            Relative Date Ranges
          </Typography>

          <List disablePadding>
            {predefinedRanges.map((item) => (
              <ListItemButton
                key={item.value}
                selected={`T-${customDays}`}
                onClick={() => {
                  setCustomDays(item.days);
                  onReceiveProps(item.days);
                }}
                sx={{
                  borderRadius: "6px",
                  mb: 0.5,
                }}
              >
                <CalendarMonthIcon
                  sx={{
                    fontSize: 18,
                    mr: 1,
                  }}
                />

                <ListItemText
                  primary={`${item.label} (${item.value})`}
                />
              </ListItemButton>
            ))}

            {/* Custom Range */}
            <ListItemButton
              selected={selectedRange}
              onClick={() => {
                setSelectedRange("custom");
              }}
              sx={{
                borderRadius: "6px",
                alignItems: "flex-start",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <TuneIcon
                  sx={{
                    fontSize: 18,
                    mr: 1,
                  }}
                />

                <Typography>
                  Custom Range
                </Typography>
              </Box>

              {selectedRange === "custom" && (
                <Box
                  sx={{
                    mt: 2,
                    ml: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography>
                    Last X Days:
                  </Typography>

                  <TextField
                    size="small"
                    value={customDays}
                    type="number"
                    onChange={(e) => {
                        let value = Number(e.target.value);
                        if (isNaN(value)) return;
                        if (value < 1) value = 1;
                        if (value > 30) value = 30;
                        setCustomDays(value);
                        onReceiveProps(value);
                    }}
                    sx={{
                      width: "80px",
                    }}
                  />

                  <Typography>
                    {`(T-${customDays})`}
                  </Typography>
                </Box>
              )}
            </ListItemButton>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Live Preview */}
          <Typography
            sx={{
              color: "#777",
              fontSize: "14px",
              mb: 1,
            }}
          >
            Live Preview:
          </Typography>

          <Typography
            sx={{
              color: "#777",
              fontSize: "14px",
            }}
          >
            Current Coverage:
          </Typography>
            {coverage && (
              <Typography
                sx={{
                  fontWeight: 700,
                  mt: 0.5,
                }}
              >
                {coverage.start} - {coverage.end}
              </Typography>
            )}
        </Paper>
      )}
    </Box>
  );
}